/**
 * Reconciliation Screen
 * Compare declared vs detected expenses per category
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { financialProfilesApi } from '../api/endpoints/financialProfiles';
import { formatCurrency } from '../utils/formatters';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import type {
  ReconciliationComparison,
  ReconciliationSummary,
  ReconciliationStatus,
} from '../api/types';
import logger from '../utils/logger';

const STATUS_CONFIG: Record<ReconciliationStatus, { icon: string; color: string; label: string }> = {
  ok: { icon: 'check-circle', color: '#4CAF50', label: 'OK' },
  warning: { icon: 'alert-circle', color: '#FF9800', label: 'Écart modéré' },
  error: { icon: 'alert-circle', color: '#EF4444', label: 'Écart important' },
  no_data: { icon: 'help-circle-outline', color: '#9CA3AF', label: 'Pas de données' },
};

const CATEGORY_ICONS: Record<string, string> = {
  housing: 'home-outline',
  food: 'food-apple-outline',
  transport: 'car-outline',
  insurance: 'shield-outline',
  subscriptions: 'refresh',
  leisure: 'gamepad-variant-outline',
  health: 'hospital-box-outline',
  shopping: 'cart-outline',
};

export default function ReconciliationScreen() {
  const navigation = useNavigation();
  const [comparisons, setComparisons] = useState<ReconciliationComparison[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const response = await financialProfilesApi.getReconciliation();
      setComparisons(response.comparisons);
      setSummary(response.summary);
    } catch (err: any) {
      logger.error('Error loading reconciliation:', err);
      setError('Impossible de charger les données. Vérifie que tu as importé des relevés bancaires.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleUpdate = async (comparison: ReconciliationComparison) => {
    const amount = Math.round(comparison.detected);
    Alert.alert(
      'Mettre à jour',
      `Remplacer ${comparison.label} : ${formatCurrency(comparison.declared)}/mois par ${formatCurrency(amount)}/mois (détecté) ?`,
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Mettre à jour',
          onPress: async () => {
            try {
              setUpdatingCategory(comparison.category);
              await financialProfilesApi.reconcileCategory(comparison.category, amount);
              await loadData();
            } catch (err) {
              logger.error('Error reconciling category:', err);
              Alert.alert('Erreur', 'Impossible de mettre à jour cette catégorie.');
            } finally {
              setUpdatingCategory(null);
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Analyse en cours...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Icon name="alert-circle-outline" size={48} color={colors.textSecondary} />
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadData}>
          <Text style={styles.retryButtonText}>Réessayer</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Réconciliation budget</Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Summary Card */}
        {summary && (
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>Résumé</Text>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Dépenses déclarées</Text>
              <Text style={styles.summaryValue}>{formatCurrency(summary.total_declared)}/mois</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Dépenses détectées</Text>
              <Text style={styles.summaryValue}>{formatCurrency(summary.total_detected)}/mois</Text>
            </View>
            {summary.categories_needing_update > 0 && (
              <View style={styles.summaryAlert}>
                <Icon name="alert-circle" size={16} color="#FF9800" />
                <Text style={styles.summaryAlertText}>
                  {summary.categories_needing_update} catégorie{summary.categories_needing_update > 1 ? 's' : ''} à mettre à jour
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Subtitle */}
        <Text style={styles.subtitle}>Déclaré vs Détecté (3 derniers mois)</Text>

        {/* Category comparisons */}
        {comparisons.map((item) => {
          const config = STATUS_CONFIG[item.status];
          const categoryIcon = CATEGORY_ICONS[item.category] || 'tag-outline';
          const isUpdating = updatingCategory === item.category;

          return (
            <View key={item.category} style={styles.categoryCard}>
              <View style={styles.categoryHeader}>
                <View style={[styles.categoryIconContainer, { backgroundColor: `${config.color}15` }]}>
                  <Icon name={categoryIcon} size={20} color={config.color} />
                </View>
                <Text style={styles.categoryLabel}>{item.label}</Text>
                <Icon name={config.icon} size={20} color={config.color} />
              </View>

              <View style={styles.amountsRow}>
                <View style={styles.amountColumn}>
                  <Text style={styles.amountLabel}>Déclaré</Text>
                  <Text style={styles.amountValue}>{formatCurrency(item.declared)}/mois</Text>
                </View>
                <View style={styles.amountColumn}>
                  <Text style={styles.amountLabel}>Détecté</Text>
                  <Text style={[
                    styles.amountValue,
                    item.status === 'no_data' && styles.amountNoData,
                  ]}>
                    {item.status === 'no_data'
                      ? 'Pas de données'
                      : `${formatCurrency(item.detected)}/mois`}
                  </Text>
                </View>
              </View>

              {item.status !== 'ok' && item.status !== 'no_data' && (
                <View style={styles.diffRow}>
                  <Text style={[styles.diffText, { color: config.color }]}>
                    Écart : {item.difference > 0 ? '+' : ''}{formatCurrency(item.difference)}
                  </Text>
                </View>
              )}

              {item.needs_update && (
                <TouchableOpacity
                  style={styles.updateButton}
                  onPress={() => handleUpdate(item)}
                  disabled={isUpdating}
                  activeOpacity={0.7}
                >
                  {isUpdating ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <>
                      <Icon name="sync" size={16} color={colors.primary} />
                      <Text style={styles.updateButtonText}>Mettre à jour</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
            </View>
          );
        })}

        {comparisons.length === 0 && (
          <View style={styles.emptyState}>
            <Icon name="check-circle-outline" size={48} color={colors.primary} />
            <Text style={styles.emptyStateTitle}>Tout est en ordre</Text>
            <Text style={styles.emptyStateText}>
              Tes déclarations correspondent à tes dépenses réelles.
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    backgroundColor: colors.background,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  errorText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: spacing.lg,
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: {
    padding: spacing.xs,
    marginRight: spacing.sm,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  summaryCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  summaryValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  summaryAlert: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    borderRadius: 8,
    padding: spacing.sm,
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  summaryAlertText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: '#F57C00',
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  categoryCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  categoryIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  categoryLabel: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  amountsRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  amountColumn: {
    flex: 1,
  },
  amountLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  amountValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  amountNoData: {
    color: colors.textTertiary,
    fontWeight: typography.fontWeight.regular,
    fontStyle: 'italic',
  },
  diffRow: {
    marginTop: spacing.sm,
  },
  diffText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  updateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary,
    gap: spacing.xs,
  },
  updateButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  emptyStateTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginTop: spacing.md,
  },
  emptyStateText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
});
